/**
 * Artifact Panel - Claude Style
 *
 * Full-screen responsive panel with:
 * - Gradient background
 * - Header with tabs (preview/code), title, copy, close
 * - Beautiful chart/table rendering
 */

import * as React from 'react'
import { X, Eye, Code2, Copy, Check, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { ChartArtifact } from './artifacts/chart-artifact'
import { TableArtifact } from './artifacts/table-artifact'
import { cn } from '@/lib/utils'

export interface Artifact {
  id: string
  type: 'chart' | 'table' | 'export'
  title: string
  data: unknown
  generatedAt: string
}

interface ArtifactPanelProps {
  artifacts: Artifact[]
  onClose: () => void
  onExport: (artifact: Artifact, format: 'csv' | 'excel') => void
}

export function ArtifactPanel({ artifacts, onClose, onExport }: ArtifactPanelProps) {
  const [currentIndex, setCurrentIndex] = React.useState(artifacts.length - 1)
  const [viewMode, setViewMode] = React.useState<'preview' | 'code'>('preview')
  const [copied, setCopied] = React.useState(false)

  // Update current index when new artifacts arrive
  React.useEffect(() => {
    setCurrentIndex(artifacts.length - 1)
  }, [artifacts.length])

  const currentArtifact = artifacts[currentIndex]

  const handlePrev = () => {
    setCurrentIndex((prev) => Math.max(0, prev - 1))
  }

  const handleNext = () => {
    setCurrentIndex((prev) => Math.min(artifacts.length - 1, prev + 1))
  }

  const handleCopy = async () => {
    if (!currentArtifact) return
    try {
      const data = typeof currentArtifact.data === 'string'
        ? currentArtifact.data
        : JSON.stringify(currentArtifact.data, null, 2)
      await navigator.clipboard.writeText(data)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  if (!currentArtifact) return null

  // Get artifact type label
  const typeLabel = currentArtifact.type === 'chart' ? 'JSX'
    : currentArtifact.type === 'table' ? 'Data'
    : 'Export'

  return (
    <div className="h-full flex flex-col bg-gradient-to-br from-sky-50 via-blue-50 to-indigo-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border/50 bg-background/80 backdrop-blur-sm">
        {/* Left side - tabs and title */}
        <div className="flex items-center gap-3">
          {/* View Mode Tabs */}
          <div className="flex items-center gap-0.5 p-0.5 rounded-lg bg-muted/50">
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                'h-7 w-7 rounded-md',
                viewMode === 'preview' && 'bg-background shadow-sm'
              )}
              onClick={() => setViewMode('preview')}
              title="Preview"
            >
              <Eye className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                'h-7 w-7 rounded-md',
                viewMode === 'code' && 'bg-background shadow-sm'
              )}
              onClick={() => setViewMode('code')}
              title="Code"
            >
              <Code2 className="h-4 w-4" />
            </Button>
          </div>

          {/* Title */}
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm truncate max-w-[200px]">
              {currentArtifact.title}
            </span>
            <span className="text-xs text-muted-foreground">· {typeLabel}</span>
          </div>
        </div>

        {/* Right side - actions */}
        <div className="flex items-center gap-1">
          {/* Navigation for multiple artifacts */}
          {artifacts.length > 1 && (
            <>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={handlePrev}
                disabled={currentIndex === 0}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-xs text-muted-foreground px-1">
                {currentIndex + 1}/{artifacts.length}
              </span>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={handleNext}
                disabled={currentIndex === artifacts.length - 1}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              <div className="w-px h-4 bg-border mx-1" />
            </>
          )}

          {/* Copy button */}
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={handleCopy}
          >
            {copied ? (
              <>
                <Check className="h-3.5 w-3.5 mr-1 text-emerald-500" />
                Copied
              </>
            ) : (
              <>
                <Copy className="h-3.5 w-3.5 mr-1" />
                Copy
              </>
            )}
          </Button>

          {/* Close button */}
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Content */}
      <ScrollArea className="flex-1">
        <div className="p-6">
          {viewMode === 'preview' ? (
            <ArtifactRenderer
              artifact={currentArtifact}
              onExport={(format) => onExport(currentArtifact, format)}
            />
          ) : (
            <CodeView artifact={currentArtifact} />
          )}
        </div>
      </ScrollArea>
    </div>
  )
}

function ArtifactRenderer({
  artifact,
  onExport,
}: {
  artifact: Artifact
  onExport: (format: 'csv' | 'excel') => void
}) {
  // Parse data if it's a string
  const data = typeof artifact.data === 'string'
    ? JSON.parse(artifact.data)
    : artifact.data

  switch (artifact.type) {
    case 'chart':
      return (
        <div className="bg-background/90 backdrop-blur-sm rounded-2xl shadow-lg border border-border/50 p-6">
          <ChartArtifact
            artifact={data as Parameters<typeof ChartArtifact>[0]['artifact']}
            onExport={onExport}
          />
        </div>
      )
    case 'table':
      return (
        <div className="bg-background/90 backdrop-blur-sm rounded-2xl shadow-lg border border-border/50 p-6">
          <TableArtifact
            artifact={data as Parameters<typeof TableArtifact>[0]['artifact']}
            onExport={onExport}
          />
        </div>
      )
    case 'export':
      return (
        <div className="bg-background/90 backdrop-blur-sm rounded-2xl shadow-lg border border-border/50 p-6">
          <h3 className="font-medium mb-2">{artifact.title}</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Data ready for export
          </p>
          <div className="flex gap-2">
            <Button size="sm" onClick={() => onExport('csv')}>
              Download CSV
            </Button>
            <Button size="sm" variant="outline" onClick={() => onExport('excel')}>
              Download Excel
            </Button>
          </div>
        </div>
      )
    default:
      return (
        <div className="bg-background/90 backdrop-blur-sm rounded-2xl shadow-lg border border-border/50 p-6">
          <p className="text-sm text-muted-foreground">
            Unknown artifact type: {artifact.type}
          </p>
        </div>
      )
  }
}

function CodeView({ artifact }: { artifact: Artifact }) {
  const data = typeof artifact.data === 'string'
    ? artifact.data
    : JSON.stringify(artifact.data, null, 2)

  return (
    <div className="bg-background/90 backdrop-blur-sm rounded-2xl shadow-lg border border-border/50 overflow-hidden">
      <div className="bg-muted/50 px-4 py-2 border-b border-border/50">
        <span className="text-xs font-mono text-muted-foreground">
          {artifact.type === 'chart' ? 'chart.jsx' : artifact.type === 'table' ? 'data.json' : 'export.json'}
        </span>
      </div>
      <pre className="p-4 overflow-x-auto text-sm font-mono">
        <code className="text-foreground">{data}</code>
      </pre>
    </div>
  )
}
